<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAxios } from 'dashboard/composables/useAxios';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const router = useRouter();
const axios = useAxios();
const { t } = useI18n();

const voiceAgentId = route.params.voiceAgentId;
const voiceAgent = ref(null);
const availableFlows = ref([]);
const attachedFlows = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  try {
    // Fetch voice agent details
    const { data: agentData } = await axios.get(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}`
    );
    voiceAgent.value = agentData;

    // Fetch all available flows for this account
    const { data: flowsData } = await axios.get(
      '/api/v1/accounts/:account_id/flows'
    );
    availableFlows.value = flowsData;

    // Fetch attached flows for this voice agent
    const { data: attachedData } = await axios.get(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}/flows`
    );
    attachedFlows.value = attachedData || [];
  } catch (error) {
    console.error('Error fetching data:', error);
    useAlert('Failed to load flows');
  } finally {
    isLoading.value = false;
  }
};

const isFlowAttached = flowId => {
  return attachedFlows.value.some(f => f.id === flowId);
};

const toggleFlow = async flow => {
  isSaving.value = true;
  try {
    if (isFlowAttached(flow.id)) {
      // Detach flow
      await axios.delete(
        `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}/flows/${flow.id}`
      );
      attachedFlows.value = attachedFlows.value.filter(f => f.id !== flow.id);
      useAlert(`Removed ${flow.name} from voice agent`);
    } else {
      // Attach flow
      await axios.post(
        `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}/flows`,
        {
          flow_id: flow.id,
          enabled: true,
          priority: attachedFlows.value.length + 1,
        }
      );
      attachedFlows.value.push(flow);
      useAlert(`Added ${flow.name} to voice agent`);
    }
  } catch (error) {
    console.error('Error toggling flow:', error);
    useAlert(error?.message || 'Failed to update flow');
  } finally {
    isSaving.value = false;
  }
};

const flowTypeIcon = flowType => {
  const icons = {
    food_ordering: 'i-lucide-utensils',
    restaurant: 'i-lucide-chef-hat',
    customer_support: 'i-lucide-headphones',
    appointment: 'i-lucide-calendar',
    default: 'i-lucide-workflow',
  };
  return icons[flowType] || icons.default;
};

const flowTypeColor = flowType => {
  const colors = {
    food_ordering: 'text-orange-500',
    restaurant: 'text-purple-500',
    customer_support: 'text-blue-500',
    appointment: 'text-green-500',
    default: 'text-slate-500',
  };
  return colors[flowType] || colors.default;
};

onMounted(() => {
  fetchData();
});
</script>

<template>
  <PageLayout
    :header-title="`${voiceAgent?.name || 'Voice Agent'} - Flows`"
    :show-pagination-footer="false"
    :is-fetching="isLoading"
    :show-know-more="false"
    :back-url="{ name: 'aivoice_voice_agents_edit', params: { voiceAgentId } }"
  >
    <template #body>
      <div v-if="voiceAgent" class="flex flex-col gap-6 max-w-4xl">
        <!-- Header -->
        <div class="flex flex-col gap-2">
          <h2 class="text-lg font-semibold text-n-slate-12">
            Conversation Flows
          </h2>
          <p class="text-sm text-n-slate-11">
            Select which flows your voice agent should use. Flows define
            structured conversation patterns for specific scenarios like food
            ordering, appointments, or customer support.
          </p>
        </div>

        <!-- Attached Flows Summary -->
        <div
          v-if="attachedFlows.length > 0"
          class="flex flex-col gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        >
          <div class="flex items-center gap-2">
            <span
              class="i-lucide-check-circle text-blue-600 dark:text-blue-400"
            />
            <span class="text-sm font-medium text-blue-900 dark:text-blue-100">
              {{ attachedFlows.length }}
              {{ attachedFlows.length === 1 ? 'flow' : 'flows' }} enabled
            </span>
          </div>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="flow in attachedFlows"
              :key="flow.id"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full"
            >
              <span class="text-xs" :class="[flowTypeIcon(flow.flow_type)]" />
              {{ flow.name }}
            </span>
          </div>
        </div>

        <!-- No Flows Message -->
        <div
          v-else
          class="flex flex-col items-center gap-4 p-12 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-center"
        >
          <div
            class="flex items-center justify-center w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl shadow-lg"
          >
            <span
              class="i-lucide-workflow text-3xl text-blue-600 dark:text-blue-400"
            />
          </div>
          <div class="flex flex-col gap-2">
            <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">
              No flows enabled yet
            </h3>
            <p class="text-sm text-slate-600 dark:text-slate-400 max-w-md">
              Enable flows below to give your voice agent structured
              conversation capabilities.
            </p>
          </div>
        </div>

        <!-- Available Flows List -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-n-slate-12">
              Available Flows
            </h3>
            <Button
              color="blue"
              size="sm"
              @click="router.push({ name: 'aivoice_flows_index' })"
            >
              <span class="i-lucide-plus mr-1" />
              Create Flow
            </Button>
          </div>

          <div
            v-if="availableFlows.length === 0 && !isLoading"
            class="flex flex-col items-center gap-4 p-12 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/20 dark:to-slate-800/20 border border-slate-200 dark:border-slate-700 rounded-xl text-center"
          >
            <div
              class="flex items-center justify-center w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl shadow-lg"
            >
              <span
                class="i-lucide-inbox text-3xl text-slate-400 dark:text-slate-500"
              />
            </div>
            <div class="flex flex-col gap-2">
              <h4 class="text-lg font-bold text-slate-900 dark:text-slate-100">
                No flows found
              </h4>
              <p class="text-sm text-slate-600 dark:text-slate-400 max-w-md">
                Create your first flow to enable structured conversations for
                your voice agent.
              </p>
            </div>
            <Button
              color="blue"
              size="md"
              @click="router.push({ name: 'aivoice_flows_index' })"
            >
              <span class="i-lucide-plus mr-2" />
              Create Your First Flow
            </Button>
          </div>

          <div v-else class="grid grid-cols-1 gap-4">
            <label
              v-for="flow in availableFlows"
              :key="flow.id"
              class="flex items-start gap-4 p-4 bg-n-solid-2 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md"
              :class="
                isFlowAttached(flow.id)
                  ? 'border-blue-500 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-n-weak hover:border-n-container'
              "
            >
              <!-- Checkbox -->
              <input
                type="checkbox"
                :checked="isFlowAttached(flow.id)"
                :disabled="isSaving"
                class="mt-1 w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                @change="toggleFlow(flow)"
              />

              <!-- Flow Icon -->
              <div
                class="flex items-center justify-center w-10 h-10 bg-n-alpha-3 rounded-lg flex-shrink-0"
              >
                <span
                  class="text-xl"
                  :class="[
                    flowTypeIcon(flow.flow_type),
                    flowTypeColor(flow.flow_type),
                  ]"
                />
              </div>

              <!-- Flow Details -->
              <div class="flex flex-col gap-1 flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ flow.name }}
                  </h4>
                  <span
                    v-if="flow.flow_type"
                    class="px-2 py-0.5 bg-n-alpha-3 text-n-slate-11 text-xs font-medium rounded-full"
                  >
                    {{ flow.flow_type }}
                  </span>
                  <span
                    v-if="isFlowAttached(flow.id)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full"
                  >
                    <span class="i-lucide-check text-xs" />
                    Enabled
                  </span>
                </div>
                <p
                  v-if="flow.description"
                  class="text-sm text-n-slate-11 line-clamp-2"
                >
                  {{ flow.description }}
                </p>
                <p v-else class="text-sm text-n-slate-10 italic">
                  No description
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
